import { Entity } from "src/app/shared/types/entity.type";
import { Company } from "../companies/company.type";
import { ConfigurableOption } from "../configurable-options/configurable-options.type";
import { Contract } from "../contracts/contract.type";
import { InternalProvider } from "../internal-provider/internal-provider.type";
import { Location } from "../locations/location.type";
import { QuotedAsset } from "../quotes/quote.type";
import { Ticket } from "../tickets/ticket.type";

//#region Interface

export interface Asset extends Entity {
  id?: string;
  name?: string;
  serialNumber?: string;
  description?: string;
  category?: AssetCategories;
  type?: AssetType;
  typeId?: string;
  startDate?: string;
  endDate?: string;
  company?: Company;
  companyId?: string;
  contract?: Contract;
  internalProvider?: InternalProvider;
  internalProviderId?: string;
  contractId?: string;
  location?: Location;
  locationId?: string;
  coverageStatus?: string;
  createdAt?: Date;
  updatedAt?: Date;
  sla?: ConfigurableOption;
  slaId?: string;
  quotedSla?: string;
  partnerPrice?: number | string;
  clientPrice?: number | string;
  channelWorksCost?: number | string;
  quotedPrice?: number | string;
  quotedTerm?: AssetQuotedTerms;
  quotedAssets?: QuotedAsset[];
  quotedAsset?: QuotedAsset;
  quotedAssetId?: string;
  previousChannelWorksCost?: number;
  previousPartnerPrice?: number;
  monitoring?: boolean;
  dmr?: boolean;
  tickets?: Ticket[];
  isPending?: boolean;
  hasConfigurationNotes?: boolean;
  fairMarketValue?: number;
  bookValue?: number;
  buyPrice?: number;
}

export interface ContractAsset extends Entity {
  id?: string;
  assetId?: string;
  asset?: Asset;
  coverageStatus?: string;
  contractId?: string;
  contract?: Contract;
  startDate?: string;
  endDate?: string;
  channelWorksCost?: number | string;
  clientPrice?: number | string;
  partnerPrice?: number | string;
  monitoring?: boolean;
  dmr?: boolean;
  internalProviderId?: string;
  internalProvider?: InternalProvider;
  slaId?: string;
  sla?: ConfigurableOption;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AssetRenewal {
  id?: string;
  name?: string;
  description?: string;
  sla?: ConfigurableOption;
  slaId?: string;
  category?: AssetCategories;
  startDate?: moment.Moment;
  endDate?: moment.Moment;
  previousChannelWorksCost?: number;
  channelWorksCost?: number;
  previousPartnerPrice?: number;
  partnerPrice?: number;
  dmr?: boolean;
  monitoring?: boolean;
  internalProvider?: InternalProvider;
  internalProviderId?: string;
}

export interface TotalContractAssetsPrices {
  readonly totalPartnerPrice: number;
  readonly totalChannelWorksCost: number;
}

export interface AssetCountByCoverageStatus {
  pending?: number;
  active?: number;
  terminated?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "not covered"?: number;
  expired?: number;
  decommissioned?: number;
}

export interface AssetAttachment extends Entity {
  id: string;
  name: string;
  url: string;
  description: string;
  assetId: string;
  notes: string;
  contract: Contract;
  file?: File;
  extension?: string;
}

export interface AssetOem extends Entity {
  id: string;
  name: string;
  status: AssetOemStatuses;
  externalId?: number;
}

export interface AssetModel extends Entity {
  id: string;
  name: string;
  status: AssetModelStatuses;
  oem: AssetOem;
  oemId: string;
  externalId?: number;
}

export interface AssetType extends Entity {
  id: string;
  name: string;
  status: AssetTypeStatuses;
  model: AssetModel;
  modelId: string;
  externalId?: number;
}

export interface CalculatorAssetOem {
  externalId: number;
  name: string;
  status: string;
}

export interface CalculatorAssetModel {
  externalId: number;
  name: string;
  status: string;
}

export interface CalculatorAssetType {
  externalId: number;
  name: string;
  status: string;
}

export interface CalculatorAssetOemRequest {
  name: string;
  status: string;
}

export interface CalculatorAssetModelRequest {
  name: string;
  status: string;
  oemExternalId: number;
}

export interface CalculatorAssetTypeRequest {
  name: string;
  status: string;
  oemExternalId: number;
  modelExternalId: number;
}

export interface AssetOemModelType {
  oemId: string;
  oemExternalId: number;
  oemName: string;
  oemStatus: AssetOemStatuses;
  modelId: string;
  modelExternalId: number;
  modelName: string;
  modelStatus: AssetModelStatuses;
  typeId: string;
  typeExternalId: number;
  typeName: string;
  typeStatus: AssetTypeStatuses;
}

export interface AssetOemModelTypeRequest {
  oemId?: number;
  modelId?: number;
  oemName?: string;
  modelName?: string;
  typeName: string;
}

export interface AssetAttribute extends Entity {
  id: string;
  quantity: number;
  default: boolean;
  assetId: string;
  asset: Asset;
  assetTypeId: string;
  assetType: AssetType;
  attributeId?: string;
  attribute?: Attribute;
  category?: AssetCategories;
  deleted?: boolean;
}

export interface Attribute extends Entity {
  id: string;
  name: string;
  type: string;
  buyPrice: number;
}

//#endregion

//#region Type
export type UpdateAssets = {
  [key in keyof Pick<Asset, "sla" | "startDate" | "channelWorksCost" | "partnerPrice">]: {
    value: string | number;
    operator: "=" | "-" | "%";
  };
};
//#endregion

//#region Enum

export enum AssetCategories {
  subscription = "subscription",
  workstations = "workstations",
  server = "server",
  storage = "storage",
  networking = "networking",
  other = "other",
  unassigned = "unassigned",
  mainframe = "mainframe & midrange",
}

export enum AssetQuotedTerms {
  monthly = "monthly",
  quarterly = "quarterly",
  yearly = "yearly",
}

export enum AssetCoverageStatuses {
  pending = "pending",
  active = "active",
  terminated = "terminated",
  notcovered = "not covered",
  expired = "expired",
  decommissioned = "decommissioned",
  replacedDuringRepair = "replaced during repair",
}

export enum AssetOemStatuses {
  official = "official",
  unofficial = "unofficial",
  invalid = "invalid",
}

export enum AssetModelStatuses {
  official = "official",
  unofficial = "unofficial",
  invalid = "invalid",
}

export enum AssetTypeStatuses {
  official = "official",
  unofficial = "unofficial",
  invalid = "invalid",
}
//#endregion
